import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/api/client';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { Printer, Plus, Edit, Trash } from 'lucide-react';
import { printDocument, buildInvoiceContent } from '@/utils/printDocs';
import { InvoiceForm } from '@/components/forms/InvoiceForm';
import { useAuth } from '@/context/AuthContext';
import { formatNumber, INVOICE_STATUSES } from '@/utils/helpers';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';

export const Invoices = () => {
  const currency = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState({});
  const [productsMap, setProductsMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invRes, custRes, prodRes] = await Promise.all([
        api.getInvoices(),
        api.getCustomers(),
        api.getProducts()
      ]);
      // Sort by created_at desc
      const sorted = invRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInvoices(sorted);
      const custMap = {};
      custRes.data.forEach(c => { custMap[c.id] = c; });
      setCustomers(custMap);
      const prodMap = {};
      (prodRes.data || []).forEach(p => { prodMap[p.id] = p; });
      setProductsMap(prodMap);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const filteredInvoices = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return invoices.filter(inv => {
      if (statusFilter && inv.status !== statusFilter) return false;
      const created = inv.created_at ? new Date(inv.created_at).toISOString().split('T')[0] : '';
      if (dateFrom && created < dateFrom) return false;
      if (dateTo && created > dateTo) return false;
      if (!q) return true;
      const customer = customers[inv.customer_id];
      return inv.id.toLowerCase().includes(q) ||
        customer?.name?.toLowerCase().includes(q) ||
        inv.notes?.toLowerCase().includes(q);
    });
  }, [invoices, searchTerm, statusFilter, dateFrom, dateTo, customers]);

  const handlePrint = (invoice) => {
    const customer = customers[invoice.customer_id];
    // Enrich each line item with the product's default warranty (months) for warranty display.
    // If the sold serial has its own recorded warranty, prefer that.
    const enrichedItems = (invoice.items || []).map((it) => {
      const p = productsMap[it.product_id];
      let warranty = p?.warranty || 0;
      if (it.serial && p?.serials) {
        const sMeta = p.serials.find(s => s.sn === it.serial);
        if (sMeta && sMeta.warranty_months) warranty = sMeta.warranty_months;
      }
      return { ...it, warranty };
    });
    const content = buildInvoiceContent({
      invoice,
      customer,
      items: enrichedItems
    });
    printDocument({ title: `Invoice-${invoice.id.slice(0, 8)}`, content, docType: 'invoice' });
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setShowForm(true);
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.deleteInvoice(invoice.id);
      loadData();
    } catch (error) {
      alert('Failed to delete invoice');
    }
  };

  const handleNew = () => {
    setEditingInvoice(null);
    setShowForm(true);
  };

  const handleSaved = () => {
    loadData();
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Draft': 'default',
      'Ready for Collection': 'warning',
      'Completed': 'success'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invoices</h1>
        <Button onClick={handleNew} data-testid="new-invoice-button">
          <Plus size={20} className="mr-2" />
          New Invoice
        </Button>
      </div>

      <Card>
        <FilterBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by invoice ID, customer, or notes..."
          filters={[{
            key: 'status',
            label: 'All Statuses',
            value: statusFilter,
            options: INVOICE_STATUSES,
            onChange: setStatusFilter
          }]}
          dateRange={{
            from: dateFrom,
            to: dateTo,
            onFromChange: setDateFrom,
            onToChange: setDateTo
          }}
          onClear={() => { setSearchTerm(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
          onExport={() => exportToCSV(filteredInvoices, 'invoices', CSV_COLUMNS.invoices)}
          testId="invoices-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                  {invoices.length === 0 ? 'No invoices found. Click "New Invoice" to create one.' : 'No invoices match the filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((invoice) => {
                const balance = invoice.total - (invoice.paid_amount || 0);
                return (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium font-mono">{invoice.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{customers[invoice.customer_id]?.name || 'Unknown'}</TableCell>
                    <TableCell>{invoice.items?.length || 0}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(invoice.total || 0, currency)}</TableCell>
                    <TableCell>{formatCurrency(invoice.paid_amount || 0, currency)}</TableCell>
                    <TableCell>
                      <Badge variant={balance > 0 ? 'warning' : 'success'}>
                        {formatCurrency(balance, currency)}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell className="text-xs text-gray-600 dark:text-gray-400">{invoice.created_by_name || '—'}</TableCell>
                    <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(invoice)}
                          data-testid={`edit-invoice-${invoice.id}`}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePrint(invoice)}
                          data-testid={`print-invoice-${invoice.id}`}
                          title="Print"
                        >
                          <Printer size={16} />
                        </Button>
                        {isSuperAdmin() && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(invoice)}
                            data-testid={`delete-invoice-${invoice.id}`}
                            title="Delete"
                          >
                            <Trash size={16} className="text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <InvoiceForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        invoice={editingInvoice}
        onSaved={handleSaved}
      />
    </div>
  );
};
